import { useQuery } from '@tanstack/react-query';

import {
  fetchSolverProfiles,
  type SolverProfile
} from '@api-service/recommendations';
import { useAccessToken } from '@hooks/useAccessToken';
import { CUSTOM_PROFILE_ID } from '@lib/transfers/store/preferences-slice';
import { useAITransfersStore } from '@lib/transfers/store/useAITransfersStore';
import { repairPersistedConfiguration } from '@lib/transfers/utils/solver-configuration';

const FIVE_MINUTES_IN_MS = 5 * 60 * 1000;

export type ResolvedPreferences = {
  configuration: SolverProfile['configuration'] | undefined;
  isReady: boolean;
};

/** The backend's solver profiles; shared by the manager profile modal. */
export const useSolverProfiles = () => {
  const { accessToken } = useAccessToken();
  return useQuery({
    queryKey: ['solverProfiles'],
    queryFn: () => fetchSolverProfiles(accessToken as string),
    enabled: Boolean(accessToken),
    staleTime: FIVE_MINUTES_IN_MS,
    refetchOnWindowFocus: false
  });
};

/**
 * Resolves the solver configuration sent with recommendation requests:
 * the member's custom configuration when they have tuned one, else their
 * chosen preset profile, else the backend's default profile.
 */
export const useResolvedPreferences = (): ResolvedPreferences => {
  const { data, isSuccess } = useSolverProfiles();

  const selectedProfileId = useAITransfersStore(
    (state) => state.selectedProfileId
  );
  const customConfiguration = useAITransfersStore(
    (state) => state.customConfiguration
  );

  const profiles = data?.data ?? [];
  const defaultProfile =
    profiles.find((profile) => profile.isDefault) ?? profiles[0] ?? null;

  if (!isSuccess || !defaultProfile) {
    return { configuration: undefined, isReady: false };
  }

  // The store already repairs on hydration; repairing again here is a
  // cheap guard for a configuration written after hydration. A configuration
  // that cannot be repaired falls through to the default profile below.
  const repairedCustomConfiguration =
    selectedProfileId === CUSTOM_PROFILE_ID && customConfiguration
      ? repairPersistedConfiguration(customConfiguration)
      : null;
  if (repairedCustomConfiguration) {
    return { configuration: repairedCustomConfiguration, isReady: true };
  }

  const matchedProfile = profiles.find(
    (profile) => profile.name === selectedProfileId
  );

  return {
    configuration:
      matchedProfile?.configuration ?? defaultProfile.configuration,
    isReady: true
  };
};
